import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Button from '../Components/Button';

function renderBoldText(text) {
  const parts = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(<strong key={`b-${match.index}`}>{match[1]}</strong>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

function renderTextWithLinksAndFormatting(text) {
  const linkRegex = /\{\{([^|}]+)\|([^}]+)\}\}/g;
  const nodes = [];
  let lastIndex = 0;
  let match;
  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const segment = text.slice(lastIndex, match.index);
      nodes.push(...renderBoldText(segment));
    }
    const label = match[1].trim();
    const href = match[2].trim();
    nodes.push(
      <a key={`a-${match.index}`} href={href} target="_blank" rel="noopener noreferrer">{label}</a>
    );
    lastIndex = linkRegex.lastIndex;
  }
  if (lastIndex < text.length) {
    const tail = text.slice(lastIndex);
    nodes.push(...renderBoldText(tail));
  }
  return nodes;
}

function selectLocalizedText(payload, language) {
  if (!payload) return '';
  const langKey = typeof language === 'string' ? language.toUpperCase() : '';
  const localized = langKey && payload[langKey] ? payload[langKey] : null;
  if (typeof localized === 'string' && localized.length > 0) return localized;
  if (typeof payload.text === 'string' && payload.text.length > 0) return payload.text;
  if (typeof payload.EN === 'string' && payload.EN.length > 0) return payload.EN;
  return '';
}

export const hasEndScreens = (screens) => Array.isArray(screens) && screens.length > 0;

export function normalizeEndScreens(input) {
  try {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (typeof input === 'string') {
      const trimmed = input.trim();
      const semiIdx = trimmed.indexOf(';');
      const jsonPart = semiIdx > -1 ? trimmed.slice(semiIdx + 1).trim() : trimmed;
      const parsed = JSON.parse(jsonPart);
      return Array.isArray(parsed) ? parsed : [];
    }
    if (typeof input === 'object') {
      const a = input?.custom_end_screens
        ?? input?.end_screens
        ?? input?.questions?.custom_end_screens
        ?? input?.questions?.end_screens;
      return normalizeEndScreens(a);
    }
  } catch (e) {}
  return [];
}

const EndScreens = ({
  screens = [],
  language = 'EN',
  onComplete = () => {},
  showTitles = true,
  className,
}) => {
  const validScreens = useMemo(() => {
    const arr = normalizeEndScreens(screens);
    return Array.isArray(arr) ? arr.filter(s => s && Array.isArray(s.contents)) : [];
  }, [screens]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [disableNext, setDisableNext] = useState(false);

  const allCheckboxDefaults = useMemo(() => {
    const map = {};
    validScreens.forEach(screen => {
      (screen.contents || []).forEach(c => {
        if (c?.type === 'checkbox_text') {
          map[c.id] = !!c?.payload?.checkedByDefault;
        }
      });
    });
    return map;
  }, [validScreens]);

  const [checkedMap, setCheckedMap] = useState(allCheckboxDefaults);

  useEffect(() => {
    setCheckedMap(allCheckboxDefaults);
  }, [allCheckboxDefaults]);

  const currentScreen = validScreens[currentIndex] || null;
  const checkboxItems = useMemo(() => (currentScreen?.contents || []).filter(c => c?.type === 'checkbox_text'), [currentScreen]);

  useEffect(() => {
    if (checkboxItems.length > 0) {
      const allChecked = checkboxItems.every(c => checkedMap[c.id]);
      setDisableNext(!allChecked);
    } else {
      setDisableNext(false);
    }
  }, [checkboxItems, checkedMap]);

  const handleToggle = useCallback((checkboxId) => {
    setCheckedMap(prev => ({ ...prev, [checkboxId]: !prev[checkboxId] }));
  }, []);

  const handleNext = () => {
    if (currentIndex < validScreens.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  if (!hasEndScreens(validScreens)) return null;

  return (
    <Container className={className} data-end-screens>
      {showTitles && (currentScreen?.title || currentScreen?.FR) && (
        <Title>{currentScreen?.[language]?.length ? currentScreen?.[language] : (currentScreen?.title || '')}</Title>
      )}

      {(currentScreen?.contents || []).map((c) => {
        if (c.type === 'h1') {
          const text = selectLocalizedText(c?.payload, language);
          return <H1 key={c.id}>{text}</H1>;
        }
        if (c.type === 'paragraph') {
          const text = selectLocalizedText(c?.payload, language) || '';
          const lines = text.split('\n');
          return (
            <Paragraph key={c.id}>
              {lines.map((line, idx) => (
                <span key={`${c.id}-l-${idx}`}>
                  {renderTextWithLinksAndFormatting(line)}
                  {idx < lines.length - 1 && <br />}
                </span>
              ))}
            </Paragraph>
          );
        }
        if (c.type === 'image') {
          const url = c?.payload?.url || '';
          const maxHeight = c?.payload?.height || null;
          return (
            <ImageContainer key={c.id}>
              <img src={url} alt="" style={maxHeight ? { maxHeight } : undefined} loading="lazy" />
            </ImageContainer>
          );
        }
        if (c.type === 'checkbox_text') {
          const text = selectLocalizedText(c?.payload, language) || '';
          const checked = !!checkedMap[c.id];
          const lines = text.split('\n');
          return (
            <CheckboxRow key={c.id}>
              <input
                type="checkbox"
                id={`cb-${c.id}`}
                checked={checked}
                onChange={() => handleToggle(c.id)}
              />
              <label htmlFor={`cb-${c.id}`}>
                {lines.map((line, idx) => (
                  <span key={`${c.id}-cb-l-${idx}`}>
                    {renderTextWithLinksAndFormatting(line)}
                    {idx < lines.length - 1 && <br />}
                  </span>
                ))}
              </label>
            </CheckboxRow>
          );
        }
        return null;
      })}

      <BottomBar>
        <div>
          {currentIndex > 0 && (
            <Button type="back" onClick={handleBack}>Back</Button>
          )}
        </div>
        <div>
          {currentIndex < validScreens.length - 1 && (
            <Button onClick={handleNext} disabled={disableNext}>Next</Button>
          )}
        </div>
      </BottomBar>
    </Container>
  );
};

export default EndScreens;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 600px;
  width: 100%;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  margin: 0 0 10px 0;
`;

const H1 = styled.h1`
  font-size: 1.75rem;
  margin: 0 0 12px 0;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  margin: 8px 0 12px 0;
  line-height: 1.4;
`;

const ImageContainer = styled.div`
  margin: 20px 0;
  display: flex;
  justify-content: flex-start;
  width: 100%;
  
  img {
    max-width: 100%;
    height: auto;
  }
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
`;

const BottomBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  background: #ffffff;
  border-top: 1px solid rgba(0,0,0,0.06);
  z-index: 1000;
`;


